import { AppIcon } from '@/components/ui/app-icon';
import { useState } from 'react';
import { CopyButton } from '@/components/ui/copy-button';
import {
  iconArrowUpRightFromSquare,
  iconCircleCheck,
  iconTriangleExclamation,
} from '@/lib/icons';
import { cn } from '@/lib/utils';
import type {
  DnsRecord,
  DomainRow,
  DomainRowState,
} from './connect-mailbox.util';

const CHIP: Record<DomainRowState | 'checking', string> = {
  found:
    'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-300',
  missing:
    'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-300',
  incomplete:
    'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-300',
  unknown: 'border-border bg-muted text-muted-foreground',
  checking: 'border-border bg-muted text-muted-foreground',
};

export function StateChip({
  state,
  children,
}: {
  state: DomainRowState | 'checking';
  children: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex h-5 shrink-0 items-center rounded border px-1.5 text-[10px] font-medium',
        CHIP[state]
      )}
    >
      {children}
    </span>
  );
}

export function CheckIcon({ ok }: { ok: boolean }) {
  return (
    <AppIcon
      icon={ok ? iconCircleCheck : iconTriangleExclamation}
      className={
        ok
          ? 'text-emerald-600 dark:text-emerald-400'
          : 'text-amber-600 dark:text-amber-400'
      }
      style={{ fontSize: 15 }}
    />
  );
}

function RecordField({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex items-center gap-2 py-1'>
      <span className='w-10 shrink-0 text-[11px] text-muted-foreground'>
        {label}
      </span>
      <code className='min-w-0 flex-1 text-xs break-all'>{value}</code>
      <CopyButton
        value={value}
        size='sm'
        aria-label={`Copy ${label.toLowerCase()}`}
      />
    </div>
  );
}

function RecordBlock({ record }: { record: DnsRecord }) {
  return (
    <div className='mt-2 rounded-lg bg-muted/50 px-3 py-1.5'>
      {record.appendTo && (
        <p className='pt-1 text-[11px] text-muted-foreground'>
          Add to your existing SPF record
        </p>
      )}
      <RecordField label='Type' value={record.type} />
      <RecordField label='Host' value={record.host} />
      <RecordField label='Value' value={record.value} />
    </div>
  );
}

export function DomainRowView({ row }: { row: DomainRow }) {
  const [shown, setShown] = useState(false);
  const ok = row.state === 'found';
  return (
    <li className='px-3.5 py-2.5'>
      <div className='flex items-center gap-2.5 text-sm'>
        <CheckIcon ok={ok} />
        <span className='min-w-0 flex-1'>{row.label}</span>
        <StateChip state={row.state}>{row.chip}</StateChip>
        {row.record && (
          <button
            type='button'
            aria-expanded={shown}
            onClick={() => setShown((value) => !value)}
            className='shrink-0 text-xs font-medium text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring'
          >
            {shown ? 'Hide record' : 'Show record'}
          </button>
        )}
        {row.guide && (
          <a
            href={row.guide}
            target='_blank'
            rel='noreferrer noopener'
            className='inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring'
          >
            Setup guide
            <AppIcon
              icon={iconArrowUpRightFromSquare}
              style={{ fontSize: 9 }}
              aria-hidden
            />
          </a>
        )}
      </div>
      {row.record && shown && <RecordBlock record={row.record} />}
    </li>
  );
}
